/**
 * Event form for create/edit workflow
 * Handles event details with category selection and publish toggle
 */
import { useState } from 'react'
import { Button } from './ui/Button'
import { Input } from './ui/Input'
import { Alert } from './ui/Alert'
import { RichTextEditor } from './RichTextEditor'
import { sanitizeHTML } from '../utils/sanitize'
import type { Event, EventInput } from '../types'

interface EventFormProps {
  event?: Event
  onSubmit: (data: EventInput) => Promise<void>
  onCancel: () => void
  isSubmitting?: boolean
}

export function EventForm({ event, onSubmit, onCancel, isSubmitting }: EventFormProps) {
  const [formData, setFormData] = useState<EventInput>({
    title: event?.title || '',
    date: event?.date || '',
    end_date: event?.end_date || '',
    location: event?.location || '',
    description: event?.description || '',
    category: event?.category || undefined,
    published: event?.published ?? 1,
  })
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }

    if (!formData.date.trim()) {
      setError('Date is required')
      return
    }

    if ((formData.description?.length ?? 0) > 2000) {
      setError(
        `Description is too long (${(formData.description?.length ?? 0).toLocaleString()} characters). Maximum is 2,000.`
      )
      return
    }

    try {
      // Convert datetime-local to RFC 3339 format
      const toISO = (datetime: string | undefined): string | undefined => {
        if (!datetime) return undefined
        return new Date(datetime).toISOString()
      }

      const sanitizedData = {
        ...formData,
        date: toISO(formData.date)!,
        end_date: toISO(formData.end_date),
        description: formData.description ? sanitizeHTML(formData.description) : undefined,
      }
      await onSubmit(sanitizedData)
    } catch (err) {
      setError((err as Error).message)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Home Game vs Lincoln High"
          maxLength={200}
          required
        />
      </div>

      {/* Date */}
      <div>
        <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
          Date & Time <span className="text-red-500">*</span>
        </label>
        <Input
          id="date"
          type="datetime-local"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
        />
      </div>

      {/* End Date */}
      <div>
        <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-1">
          End Date & Time (optional)
        </label>
        <Input
          id="end_date"
          type="datetime-local"
          value={formData.end_date}
          onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">
          For multi-day events or events with specific end times
        </p>
      </div>

      {/* Location */}
      <div>
        <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
          Location
        </label>
        <Input
          id="location"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder="Roosevelt High School Gym"
          maxLength={200}
        />
      </div>

      {/* Category */}
      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
          Category
        </label>
        <select
          id="category"
          value={formData.category || ''}
          onChange={(e) =>
            setFormData({
              ...formData,
              category: e.target.value as EventInput['category'],
            })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-maroon-600"
        >
          <option value="">Select category...</option>
          <option value="game">Game</option>
          <option value="practice">Practice</option>
          <option value="competition">Competition</option>
          <option value="fundraiser">Fundraiser</option>
          <option value="community">Community</option>
        </select>
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <RichTextEditor
          content={formData.description || ''}
          onChange={(html) => setFormData({ ...formData, description: html })}
          minHeight="200px"
        />
        <p className="text-xs text-gray-500 mt-1">Rich text with formatting and links</p>
      </div>

      {/* Published Toggle */}
      <div className="border-t pt-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.published === 1}
            onChange={(e) => setFormData({ ...formData, published: e.target.checked ? 1 : 0 })}
            className="w-4 h-4 text-maroon-600 border-gray-300 rounded focus:ring-maroon-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Published {formData.published === 1 ? '✅' : '📝'}
          </span>
        </label>
        <p className="text-xs text-gray-500 mt-1">
          {formData.published === 1
            ? 'Event is visible on the public calendar'
            : 'Draft - not visible to the public'}
        </p>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : event ? 'Update Event' : 'Create Event'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
